<template>
  <BDLayout>
    <div class="transaction">
      <section id="bd-opportunities">
        <div class="container">
          <h6 class="fw-bold mb-2">Business Development - Opportunities</h6>
         
          <!-- Stats cards (Pipeline, Active, Submitted, Awarded) -->
          <div class="warehouse-inventory-card-wrapper mb-3 bd-fade-in">
            <div class="warehouse-inventory-card">
              <p>Pipeline<br />Value</p>
              <strong>{{ formatCurrency(stats.pipelineValue) }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Active<br />Opportunities</p>
              <strong>{{ stats.active || 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Submitted<br />Bids (YTD)</p>
              <strong>{{ stats.submitted || 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Win<br />Rate</p>
              <strong>{{ stats.winRate || 0 }}%</strong>
            </div>
          </div>

          <!-- Table -->
          <div class="products-list transaction-products-list bd-fade-in" style="margin-top: 0;">
            <h6 class="fw-bold text-sm">Opportunities</h6>

            <SkeletonLoader v-if="loading" type="table" :rows="8" :columns="11"></SkeletonLoader>

            <table
              v-else
              id="opportunitiesTable"
              ref="opportunitiesTable"
              class="table table-sm table-hover mb-0"
            >
              <thead>
                <tr>
                  <th>#</th>
                  <th>Ref</th>
                  <th>Title</th>
                  <th>Client</th>
                  <th>Sector</th>
                  <th>Country</th>
                  <th>Compliance</th>
                  <th>Status</th>
                  <th>Submission Deadline</th>
                  <th>Pipeline Value</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(op, idx) in opportunities" :key="op.id" @click="view(op)" style="cursor: pointer;">
                  <td>{{ idx + 1 }}</td>
                  <td>{{ op.tenderRefNo || op.referenceNo }}</td>
                  <td>{{ op.title }}</td>
                  <td>{{ op.client }}</td>
                  <td>{{ op.sector }}</td>
                  <td>{{ op.country }}</td>
                  <td>
                    <span
                      v-if="op.complianceHealth || op.complianceStatus"
                      :class="['badge', complianceBadgeClass(op.complianceHealth || op.complianceStatus)]"
                    >
                      {{ complianceLabel(op.complianceHealth || op.complianceStatus) }}
                    </span>
                    <span v-else class="text-muted small">-</span>
                  </td>
                  <td>
                    <span :class="['badge', statusBadgeClass(op.status)]">{{ humanStatus(op.status) }}</span>
                  </td>
                  <td>{{ formatDate(op.submissionDeadline) }}</td>
                  <td>{{ formatCurrency(op.estimatedValue) }}</td>
                  <td>
                    <button class="btn btn-sm btn-outline-primary bd-btn-scale" @click.stop="view(op)">
                      <i class="fas fa-arrow-right"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="!loading && opportunities.length === 0">
                  <td colspan="11" class="text-center text-muted">No opportunities found.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import { ref, reactive, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import BDLayout from '@/components/BDLayout.vue'
import SkeletonLoader from '@/components/SkeletonLoader'
import { bidApi, API_BASE_URL } from '@/api/bid'
import $ from 'jquery'
import 'datatables.net-vue3'
import 'datatables.net-bs5'
import 'datatables.net-buttons-bs5'
import 'datatables.net-responsive-bs5'
import 'jszip'
import 'datatables.net-buttons/js/buttons.html5.js'
import 'datatables.net-buttons/js/buttons.colVis.js'
import 'datatables.net-buttons/js/buttons.print.js'

const debounce = (func, delay) => {
  let timeoutId
  return function (...args) {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func.apply(this, args), delay)
  }
}

export default {
  name: 'BidOpportunitiesList',
  components: { BDLayout, SkeletonLoader },
  setup() {
    const router = useRouter()
    const loading = ref(false)
    const opportunities = ref([])
    const opportunitiesTable = ref(null)
    const clientOptions = ref([])
    const sectorOptions = ref([])
    let dataTable = null
    const stats = reactive({
      pipelineValue: 0,
      active: 0,
      submitted: 0,
      winRate: 0,
    })

    const filters = reactive({
      search: '',
      client: '',
      sector: '',
      status: '',
      year: new Date().getFullYear(),
    })

    // initialize country filter from selector (if previously saved)
    try {
      const _bd = JSON.parse(localStorage.getItem('bd_selected_country') || 'null')
      if (_bd) filters.country = _bd.name || _bd.code || _bd
    } catch (e) { /* ignore localStorage parse errors */ }

    const goToCreate = () => {
      router.push({ name: 'BidOpportunityCreate' })
    }

    const loadClients = async () => {
      try {
        clientOptions.value = await bidApi.listClients()
      } catch (err) {
        console.error('Failed to load clients for filter', err)
      }
    }

    const loadSectors = async () => {
      try {
        const base = API_BASE_URL.replace(/\/+$/, '')
        const res = await fetch(base + '/sectors', {
          headers: {
            Accept: 'application/json',
            ...(localStorage.getItem('access_token')
              ? { Authorization: `Bearer ${JSON.parse(localStorage.getItem('access_token'))}` }
              : {}),
          },
        })
        if (!res.ok) return
        const data = await res.json()
        const list =
          data && typeof data === 'object' && (data.data || data.items || data.rows)
            ? data.data || data.items || data.rows
            : data
        sectorOptions.value = Array.isArray(list) ? list : []
      } catch (err) {
        console.error('Failed to load sectors for filter', err)
      }
    }

    const initializeDataTable = () => {
      try {
        if (!opportunitiesTable.value) return

        if (dataTable) {
          dataTable.destroy()
          dataTable = null
        }

        dataTable = $(opportunitiesTable.value).DataTable({
          dom: 'Bfrtip',
          responsive: true,
          language: {
            paginate: {
              previous: '<i class="fas fa-chevron-left"></i>',
              next: '<i class="fas fa-chevron-right"></i>',
            },
          },
          buttons: [
            'colvis',
            'pageLength',
            {
              extend: 'csv',
              text: 'Export CSV',
              className: 'btn btn-primary btn-sm',
              exportOptions: {
                columns: ':visible:not(:last-child)',
              },
            },
            {
              extend: 'excel',
              text: 'Export Excel',
              className: 'btn btn-primary btn-sm',
              exportOptions: {
                columns: ':visible:not(:last-child)',
              },
            },
            {
              extend: 'print',
              text: 'Print',
              className: 'btn btn-secondary btn-sm',
              exportOptions: {
                columns: ':visible:not(:last-child)',
              },
            },
          ],
        })
      } catch (error) {
        console.error('Failed to initialize Opportunities DataTable', error)
      }
    }

    // react to BD country selector changes
    const onBdCountryChanged = (e) => {
      const country = e && e.detail ? e.detail.country : null
      filters.country = country ? (country.name || country.code || country) : ''
      fetchOpportunities()
    }

    const fetchOpportunities = async () => {
      loading.value = true
      try {
        const data = await bidApi.listOpportunities({ ...filters })
        const list = data.data || data.items || data.rows || data
        opportunities.value = Array.isArray(list) ? list : []
        if (data.stats) {
          stats.pipelineValue = data.stats.pipelineValue ?? stats.pipelineValue
          stats.active = data.stats.active ?? stats.active
          stats.submitted = data.stats.submitted ?? stats.submitted
          stats.winRate = data.stats.winRate ?? stats.winRate
        }
      } catch (err) {
        console.error('Failed to load opportunities', err)
      } finally {
        loading.value = false
        await nextTick()
        initializeDataTable()
      }
    }

    const debouncedFetch = debounce(fetchOpportunities, 500)

    const clearFilters = () => {
      filters.search = ''
      filters.client = ''
      filters.sector = ''
      filters.status = ''
      filters.year = new Date().getFullYear()
      fetchOpportunities()
    }

    const view = (op) => {
      router.push({ name: 'BidOpportunityDetail', params: { id: op.id } })
    }

    const formatDate = (val) => {
      if (!val) return 'N/A'
      const d = new Date(val)
      if (Number.isNaN(d.getTime())) return 'N/A'
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    }

    const formatCurrency = (val) => {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0,
      }).format(val)
    }

    const humanStatus = (status) => {
      switch (status) {
        case 'UNDER_REVIEW': return 'Under Review'
        case 'APPROVED': return 'Approved'
        case 'REJECTED': return 'Rejected'
        case 'IN_PREPARATION': return 'In Preparation'
        case 'SUBMITTED': return 'Submitted'
        case 'AWARDED': return 'Awarded'
        case 'LOST': return 'Lost'
        default: return status || 'N/A'
      }
    }

    const complianceLabel = (status) => {
      if (!status) return 'Unknown'
      switch (status) {
        case 'HEALTHY':
        case 'GOOD':
        case 'OK':
          return 'Compliant'
        case 'WARNING':
        case 'EXPIRING_SOON':
          return 'Expiring Soon'
        case 'UNHEALTHY':
        case 'EXPIRED':
          return 'Non-Compliant'
        default:
          return status
      }
    }

    const complianceBadgeClass = (status) => {
      const label = complianceLabel(status)
      switch (label) {
        case 'Compliant':
          return 'badge-success'
        case 'Expiring Soon':
          return 'badge-warning'
        case 'Non-Compliant':
          return 'badge-danger'
        default:
          return 'badge-secondary'
      }
    }

    const statusBadgeClass = (status) => {
      switch (status) {
        case 'UNDER_REVIEW': return 'badge-warning'
        case 'APPROVED': return 'badge-success'
        case 'REJECTED': return 'badge-danger'
        case 'IN_PREPARATION': return 'badge-info'
        case 'SUBMITTED': return 'badge-primary'
        case 'AWARDED': return 'badge-success'
        case 'LOST': return 'badge-secondary'
        default: return 'badge-light'
      }
    }

    onMounted(async () => {
      // show loader immediately on page visit while we load filter data
      loading.value = true
      await Promise.all([loadClients(), loadSectors()])
      // attach global country selector listener and fetch
      window.addEventListener('bd-country-changed', onBdCountryChanged)
      // fetch opportunities (fetchOpportunities manages its own loading state)
      fetchOpportunities()
    })

    onBeforeUnmount(() => {
      if (dataTable) {
        dataTable.destroy()
        dataTable = null
      }
      window.removeEventListener('bd-country-changed', onBdCountryChanged)
    })

    return {
      loading,
      opportunities,
      stats,
      filters,
      goToCreate,
      clientOptions,
      sectorOptions,
      fetchOpportunities,
      debouncedFetch,
      clearFilters,
      view,
      formatDate,
      formatCurrency,
      humanStatus,
      statusBadgeClass,
      complianceLabel,
      complianceBadgeClass,
      opportunitiesTable,
    }
  },
}
</script>

<style scoped>
</style>
